import { Button, EmptyState, Spinner } from "@cntrl/ui";
import { orgInitials } from "../../app/hooks/useOrg.js";
import { useResolvedOrg, useRosterArtists } from "../../app/hooks/useRoster.js";
import { rosterTabLabel } from "../../shells/roster/rosterTabs.js";
import styles from "./RosterArtist.module.scss";

/**
 * The artist-required gate — shown when an artist-scoped tab is opened with NO artist selected
 * (the MVP `.artist-required` card, as a real route not CSS-injected). Offers
 * quick-pick chips (the first few roster artists) to select one. A born-blank org (no artists) shows the
 * designed empty state — NEVER placeholder artists.
 */
export function ArtistRequiredGate({
  tabKey,
  onPick,
  onManageTeam,
}: {
  tabKey: string;
  /** Select an artist to open this tab under. */
  onPick: (artistId: string) => void;
  /**
   * Open the Team surface. OPTIONAL, and the button is absent without it: Team is `roster:manage_seats`,
   * so for a staffer this was a door onto a permission wall. The CALLER decides, because this component is
   * also mounted outside an OrgProvider (its own test does) and a context read here would silently answer
   * "no" there rather than "not applicable".
   */
  onManageTeam?: () => void;
}) {
  const { orgId, subview } = useResolvedOrg();
  const artists = useRosterArtists(orgId);
  // Subview-keyed, exactly like the tab strip that routed here — `schedule` and `assets` both fork
  // their label per lens, so "Pick an artist to open X" would otherwise name a different surface
  // than the tab the person just clicked.
  const label = rosterTabLabel(tabKey, subview);

  if (artists.isLoading) {
    return (
      <div className={styles.gate} role="status" aria-live="polite">
        <Spinner size={20} label="Loading roster…" />
      </div>
    );
  }

  // A fetch FAILURE is a designed error state — never the born-blank "no artists" copy: a 500 must
  // not read as "you have no artists".
  if (artists.isError) {
    return (
      <div className={styles.gate}>
        <EmptyState
          title="We couldn't load your roster"
          description="Check your connection and try again."
          action={<Button onClick={() => artists.refetch()}>Retry</Button>}
        />
      </div>
    );
  }

  if (!artists.data || artists.data.length === 0) {
    // Born-blank: no artists to pick — guide to team management, never a fake artist.
    return (
      <div className={styles.gate}>
        <EmptyState
          title={`No artist to open ${label}`}
          description="Artists appear here once they link your organization and assign you to their team. Nothing to show until then."
          action={
            onManageTeam && (
              <Button variant="ghost" onClick={onManageTeam}>
                Manage team
              </Button>
            )
          }
        />
      </div>
    );
  }

  const picks = artists.data.slice(0, 6);
  return (
    <div className={styles.gate}>
      <p className={styles.eyebrow}>{label} · Artist-scoped</p>
      <h3 className={styles.gateTitle}>Pick an artist to open {label}</h3>
      <p className={styles.gateSub}>{label} is a per-artist view. Choose an artist from your roster.</p>
      <div className={styles.chips}>
        {picks.map((a) => (
          <button
            key={a.bandId}
            type="button"
            className={styles.chip}
            onClick={() => a.bandId && onPick(a.bandId)}
          >
            <span className={styles.chipAvatar} aria-hidden="true">
              {orgInitials(a.name)}
            </span>
            {a.name || "Artist"}
          </button>
        ))}
      </div>
    </div>
  );
}
